import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usersApi, pdvApi, docenteKeysApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  Search,
  Shield,
  UserCog,
  Pencil,
  Trash2,
  Key,
  Mail,
  Store,
  Building2,
  Check,
  Copy,
  Printer,
  Heart,
  GraduationCap,
  BookOpen,
  Lock,
  Unlock,
  Ticket,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ROLES = [
  { value: 'admin', label: 'Amministratore/RSPP', description: 'Gestione completa della sicurezza', icon: Shield },
  { value: 'collaborator', label: 'Azienda', description: 'Gestisce le proprie UL assegnate', icon: UserCog },
  { value: 'gerente', label: 'Unità Locale', description: 'Gestisce la propria UL, personale e documenti', icon: Store },
  { value: 'medico_competente', label: 'Medico Competente', description: 'Gestisce visite mediche delle UL assegnate', icon: Heart },
  { value: 'docente', label: 'Docente', description: 'Gestisce formazione e materiale didattico', icon: GraduationCap },
  { value: 'docente_online', label: 'Docente Online', description: 'Docente esterno per corsi in videoconferenza (registri pubblici, materiale, test)', icon: GraduationCap },
  { value: 'formatore', label: 'Content Creator', description: 'Crea slide e materiale didattico', icon: BookOpen },
  { value: 'verificatore', label: 'Verificatore', description: 'Esegue verifiche di terra sulle UL assegnate', icon: Shield },
  { value: 'consulente_haccp', label: 'Consulente HACCP', description: 'Gestisce autocontrollo HACCP (manuale, schede, ispezioni) per le aziende/UL assegnate o create', icon: Shield },
  { value: 'laboratorio_analisi', label: 'Laboratorio Analisi', description: 'Riceve verbali di prelievo HACCP e carica referti analitici', icon: Shield },
  { value: 'tecnico_collaboratore', label: 'Tecnico-Collaboratore', description: 'Consulente tecnico esterno. L\'admin sceglie sotto quali sezioni può vedere (aziende, UL, HACCP, formazione, studio tecnico, ecc.)', icon: UserCog },
];

// Visibilità di menu predefinita per ruolo (l'admin può poi togliere/aggiungere)
const DEFAULT_SECTIONS_BY_ROLE = {
  admin: ['dashboard', 'azienda', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'haccp_autocontrollo', 'controlli_sicurezza', 'visite_mediche', 'formazione', 'calendario', 'notifiche', 'genera_documenti', 'dvr_duvri', 'verifiche_terra', 'certificati', 'materiale_didattico'],
  azienda: ['dashboard', 'azienda', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'haccp_autocontrollo', 'controlli_sicurezza', 'visite_mediche', 'formazione', 'calendario', 'notifiche', 'genera_documenti', 'dvr_duvri', 'verifiche_terra', 'certificati'],
  collaborator: ['dashboard', 'azienda', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'haccp_autocontrollo', 'visite_mediche', 'formazione', 'certificati', 'notifiche', 'genera_documenti'],
  gerente: ['dashboard', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'haccp_autocontrollo', 'controlli_sicurezza', 'visite_mediche', 'formazione', 'certificati', 'notifiche'],
  medico_competente: ['dashboard', 'unita_locali', 'personale', 'visite_mediche', 'certificati', 'notifiche'],
  docente: ['dashboard', 'unita_locali', 'formazione', 'materiale_didattico', 'aule_virtuali', 'registri_presenze_pubblici', 'template_registri', 'test_formazione', 'certificati', 'notifiche', 'genera_documenti'],
  docente_online: ['aule_virtuali', 'registri_presenze_pubblici', 'template_registri', 'test_formazione', 'materiale_didattico', 'fatture_docenti', 'le_mie_chiavi', 'genera_documenti'],
  formatore: ['dashboard', 'materiale_didattico', 'notifiche'],
  verificatore: ['dashboard', 'unita_locali', 'verifiche_terra', 'notifiche'],
  consulente_haccp: ['dashboard', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'haccp_autocontrollo', 'controlli_sicurezza', 'certificati', 'notifiche'],
  laboratorio_analisi: ['dashboard', 'notifiche'],
  // v38e: Tecnico-Collaboratore — default: completo (l'admin ridurrà a piacere)
  tecnico_collaboratore: ['dashboard', 'azienda', 'unita_locali', 'personale', 'matrice_documenti', 'documenti', 'genera_documenti', 'dvr_duvri', 'controlli_sicurezza', 'verifiche_terra', 'calendario', 'scadenze', 'notifiche', 'haccp_autocontrollo', 'visite_mediche', 'certificati', 'formazione', 'materiale_didattico', 'aule_virtuali', 'registri_presenze_pubblici', 'template_registri', 'programmi_sicurezza', 'test_formazione', 'studio_tecnico'],
};

// Moduli disponibili per permessi configurabili
const AVAILABLE_MODULES = [
  { value: 'materiale_didattico', label: 'Materiale Didattico', description: 'Crea e gestisce slide e materiali' },
  { value: 'template_documenti', label: 'Template Documenti', description: 'Accesso ai template documenti' },
  { value: 'genera_documenti', label: 'Genera Documenti', description: 'Genera documenti da template' },
  { value: 'formazione', label: 'Formazione', description: 'Gestione corsi di formazione' },
  { value: 'registri_presenze', label: 'Registri Presenze', description: 'Gestione registri presenze' },
  { value: 'aule_virtuali', label: 'Aule Virtuali', description: 'Gestione aule virtuali' },
];

// Sezioni del menu che possono essere nascoste
const AVAILABLE_SECTIONS = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'azienda', label: 'Azienda' },
  { value: 'unita_locali', label: 'Unità Locali' },
  { value: 'personale', label: 'Personale' },
  { value: 'matrice_documenti', label: 'Matrice Documenti UL', description: 'Vista compliance documenti per UL' },
  { value: 'documenti', label: 'Documenti UL' },
  { value: 'haccp_autocontrollo', label: 'Autocontrollo HACCP', description: 'Manuale, Schede Temperature, Verbale Campioni' },
  { value: 'controlli_sicurezza', label: 'Controlli UL (sicurezza)' },
  { value: 'visite_mediche', label: 'Visite Mediche' },
  { value: 'formazione', label: 'Formazione (tutto)' },
  { value: 'aule_virtuali', label: 'Aule Virtuali' },
  { value: 'registri_presenze_pubblici', label: 'Registri Presenze Pubblici' },
  { value: 'template_registri', label: 'Template Registri' },
  { value: 'test_formazione', label: 'Test di Formazione' },
  { value: 'materiale_didattico', label: 'Materiale Didattico' },
  { value: 'fatture_docenti', label: 'Fatture Docenti' },
  { value: 'le_mie_chiavi', label: 'Le mie Chiavi (docente online)' },
  { value: 'calendario', label: 'Calendario' },
  { value: 'notifiche', label: 'Notifiche' },
  { value: 'genera_documenti', label: 'Genera Documenti' },
  { value: 'dvr_duvri', label: 'DVR/DUVRI' },
  { value: 'verifiche_terra', label: 'Verifiche di Terra' },
  { value: 'certificati', label: 'Certificati' },
  // v38c-d: nuove sezioni
  { value: 'programmi_sicurezza', label: 'Programmi Sicurezza', description: 'Catalogo programmi formativi (Generale/Specifico/Antincendio/Primo Soccorso/...)' },
  { value: 'studio_tecnico', label: 'Studio Tecnico (ERP)', description: 'Dashboard, Commesse, Clienti, Preventivi, Pagamenti, Scadenze' },
];

const UserForm = ({ user, pdvs, aziende, onSubmit, onCancel, isSuperAdmin }) => {
  const [formData, setFormData] = useState(user ? {
    email: user.email,
    full_name: user.full_name,
    phone: user.phone || '',
    role: user.role,
    allowed_pdv_ids: user.allowed_pdv_ids || [],
    allowed_azienda_ids: user.allowed_azienda_ids || [],
    allowed_modules: user.allowed_modules || [],
    extra_modules: user.extra_modules || [],
    visible_sections: user.visible_sections || [],
    password: ''
  } : {
    email: '',
    full_name: '',
    phone: '',
    role: 'collaborator',
    allowed_pdv_ids: [],
    allowed_azienda_ids: [],
    allowed_modules: [],
    extra_modules: [],
    visible_sections: DEFAULT_SECTIONS_BY_ROLE['collaborator'] || [],
    password: ''
  });

  const handlePdvToggle = (pdvId) => {
    const current = formData.allowed_pdv_ids;
    if (current.includes(pdvId)) {
      setFormData({ ...formData, allowed_pdv_ids: current.filter(id => id !== pdvId) });
    } else {
      setFormData({ ...formData, allowed_pdv_ids: [...current, pdvId] });
    }
  };

  const handleAziendaToggle = (aziendaId) => {
    const current = formData.allowed_azienda_ids;
    if (current.includes(aziendaId)) {
      setFormData({ ...formData, allowed_azienda_ids: current.filter(id => id !== aziendaId) });
    } else {
      setFormData({ ...formData, allowed_azienda_ids: [...current, aziendaId] });
    }
  };

  const handleSelectAllAziende = () => {
    if ((formData.allowed_azienda_ids || []).length === (aziende || []).length) {
      setFormData({ ...formData, allowed_azienda_ids: [] });
    } else {
      setFormData({ ...formData, allowed_azienda_ids: (aziende || []).map(a => a.id) });
    }
  };

  const handleModuleToggle = (moduleValue) => {
    const current = formData.allowed_modules || [];
    if (current.includes(moduleValue)) {
      setFormData({ ...formData, allowed_modules: current.filter(m => m !== moduleValue) });
    } else {
      setFormData({ ...formData, allowed_modules: [...current, moduleValue] });
    }
  };

  // v1.2.5: Moduli extra cross-ruolo (es. abilitare 'studio' al consulente HACCP)
  const handleExtraModuleToggle = (group) => {
    const current = formData.extra_modules || [];
    if (current.includes(group)) {
      setFormData({ ...formData, extra_modules: current.filter(m => m !== group) });
    } else {
      setFormData({ ...formData, extra_modules: [...current, group] });
    }
  };

  const handleSectionToggle = (sectionValue) => {
    const current = formData.visible_sections || [];
    if (current.includes(sectionValue)) {
      setFormData({ ...formData, visible_sections: current.filter(s => s !== sectionValue) });
    } else {
      setFormData({ ...formData, visible_sections: [...current, sectionValue] });
    }
  };

  const handleSelectAllSections = () => {
    if ((formData.visible_sections || []).length === AVAILABLE_SECTIONS.length) {
      setFormData({ ...formData, visible_sections: [] });
    } else {
      setFormData({ ...formData, visible_sections: AVAILABLE_SECTIONS.map(s => s.value) });
    }
  };

  const handleSelectAllPdvs = () => {
    if (formData.allowed_pdv_ids.length === pdvs.length) {
      setFormData({ ...formData, allowed_pdv_ids: [] });
    } else {
      setFormData({ ...formData, allowed_pdv_ids: pdvs.map(p => p.id) });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email || !formData.full_name) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    if (!user && !formData.password) {
      toast.error('Inserisci una password');
      return;
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nome Completo *</Label>
          <Input
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            placeholder="Mario Rossi"
            required
            data-testid="user-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="mario@esempio.it"
            required
            disabled={!!user}
            data-testid="user-email-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Telefono</Label>
        <Input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="+39 333 1234567"
          data-testid="user-phone-input"
        />
      </div>

      {!user && (
        <div className="space-y-2">
          <Label>Password *</Label>
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••"
            required
            data-testid="user-password-input"
          />
        </div>
      )}

      {user && (
        <div className="space-y-2">
          <Label>Nuova Password (opzionale)</Label>
          <Input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Lascia vuoto per non modificare"
            data-testid="user-password-input-edit"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Ruolo *</Label>
        <Select
          value={formData.role}
          onValueChange={(value) => {
            // Auto-popola visible_sections con i default del ruolo (solo per nuovi utenti
            // o se l'admin non aveva ancora personalizzato la visibilità)
            const defaults = DEFAULT_SECTIONS_BY_ROLE[value] || [];
            const shouldAutoFill = !user || !formData.visible_sections || formData.visible_sections.length === 0;
            setFormData({
              ...formData,
              role: value,
              visible_sections: shouldAutoFill ? defaults : formData.visible_sections,
            });
          }}
        >
          <SelectTrigger data-testid="user-role-select">
            <SelectValue placeholder="Seleziona ruolo" />
          </SelectTrigger>
          <SelectContent>
            {ROLES.filter(r => isSuperAdmin || !['super_admin', 'admin', 'amministratore', 'rspp'].includes(r.value)).map((role) => (
              <SelectItem key={role.value} value={role.value}>
                <div className="flex items-center gap-2">
                  <role.icon className="w-4 h-4" />
                  <span>{role.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {ROLES.find(r => r.value === formData.role)?.description}
        </p>
      </div>

      {['admin', 'collaborator', 'gerente', 'medico_competente', 'verificatore', 'consulente_haccp', 'tecnico_collaboratore', 'responsabile_ul'].includes(formData.role) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Unità Locali Accessibili</Label>
            <Button type="button" variant="ghost" size="sm" onClick={handleSelectAllPdvs}>
              {formData.allowed_pdv_ids.length === pdvs.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
            </Button>
          </div>
          {formData.role === 'responsabile_ul' && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded text-[11px] text-blue-900">
              ℹ️ Per i Responsabili UL seleziona <strong>esattamente una</strong> Unità Locale (quella che gestisce).
              Se la UL non compare in elenco, creala prima da <strong>Gestione Aziendale &rarr; Unità Locali</strong> &mdash;
              l&apos;utente viene creato in automatico se compili la email del referente.
            </div>
          )}
          <div className="max-h-[200px] overflow-y-auto border border-border rounded-sm p-3 space-y-2">
            {pdvs.map((pdv) => (
              <label
                key={pdv.id}
                className="flex items-center gap-3 p-2 hover:bg-secondary rounded-sm cursor-pointer"
              >
                <Checkbox
                  checked={formData.allowed_pdv_ids.includes(pdv.id)}
                  onCheckedChange={() => handlePdvToggle(pdv.id)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pdv.name}</p>
                  <p className="text-xs text-muted-foreground">{pdv.city}</p>
                </div>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {formData.allowed_pdv_ids.length} UL selezionate
          </p>
        </div>
      )}

      {(formData.role === 'consulente_haccp' || formData.role === 'tecnico_collaboratore') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-amber-600" />
              Aziende Accessibili
            </Label>
            <Button type="button" variant="ghost" size="sm" onClick={handleSelectAllAziende} data-testid="select-all-aziende-btn">
              {(formData.allowed_azienda_ids || []).length === (aziende || []).length ? 'Deseleziona tutte' : 'Seleziona tutte'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Assegnando un&apos;azienda, il collaboratore avrà accesso automaticamente a tutte le sue Unità Locali (anche future).
          </p>
          <div className="max-h-[200px] overflow-y-auto border border-border rounded-sm p-3 space-y-2" data-testid="aziende-checkbox-list">
            {(aziende || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Nessuna azienda disponibile</p>
            )}
            {(aziende || []).map((az) => (
              <label
                key={az.id}
                className="flex items-center gap-3 p-2 hover:bg-secondary rounded-sm cursor-pointer"
              >
                <Checkbox
                  checked={(formData.allowed_azienda_ids || []).includes(az.id)}
                  onCheckedChange={() => handleAziendaToggle(az.id)}
                  data-testid={`azienda-checkbox-${az.id}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{az.denominazione || az.full_name || az.ragione_sociale || az.email || `Azienda #${az.id?.slice(-6) || ''}`}</p>
                  {(az.partita_iva || az.citta || az.email) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {[az.partita_iva && `P.IVA ${az.partita_iva}`, az.citta, az.email].filter(Boolean).join(' • ')}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {(formData.allowed_azienda_ids || []).length} aziende selezionate
          </p>
        </div>
      )}

      {/* Selezione moduli per ruolo Formatore */}
      {formData.role === 'formatore' && (
        <div className="space-y-2">
          <Label>Moduli Accessibili</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Seleziona le aree a cui questo formatore può accedere
          </p>
          <div className="border border-border rounded-sm p-3 space-y-2">
            {AVAILABLE_MODULES.map((module) => (
              <label
                key={module.value}
                className="flex items-center gap-3 p-2 hover:bg-secondary rounded-sm cursor-pointer"
              >
                <Checkbox
                  checked={(formData.allowed_modules || []).includes(module.value)}
                  onCheckedChange={() => handleModuleToggle(module.value)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{module.label}</p>
                  <p className="text-xs text-muted-foreground">{module.description}</p>
                </div>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {(formData.allowed_modules || []).length} moduli selezionati
          </p>
        </div>
      )}

      {/* Sezioni visibili - solo per non-admin e non-super_admin */}
      {!['super_admin'].includes(formData.role) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Sezioni Visibili nel Menu</Label>
            <Button type="button" variant="ghost" size="sm" onClick={handleSelectAllSections}>
              {(formData.visible_sections || []).length === AVAILABLE_SECTIONS.length ? 'Deseleziona tutte' : 'Seleziona tutte'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Seleziona le sezioni che questo utente può vedere. Se nessuna è selezionata, vedrà tutte le sezioni predefinite per il suo ruolo.
          </p>
          <div className="max-h-[200px] overflow-y-auto border border-border rounded-sm p-3 space-y-1">
            {AVAILABLE_SECTIONS.map((section) => (
              <label
                key={section.value}
                className="flex items-center gap-3 p-2 hover:bg-secondary rounded-sm cursor-pointer"
              >
                <Checkbox
                  checked={(formData.visible_sections || []).includes(section.value)}
                  onCheckedChange={() => handleSectionToggle(section.value)}
                />
                <span className="text-sm">{section.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {(formData.visible_sections || []).length > 0 
              ? `${(formData.visible_sections || []).length} sezioni selezionate`
              : 'Tutte le sezioni predefinite per il ruolo'}
          </p>
        </div>
      )}

      {/* v1.2.5: Moduli extra cross-ruolo (admin abilita gruppi aggiuntivi per singolo utente) */}
      {!['super_admin'].includes(formData.role) && (
        <div className="space-y-2 border border-violet-200 bg-violet-50/50 rounded-lg p-3" data-testid="extra-modules-section">
          <Label className="text-violet-900 font-semibold">Moduli Extra Cross-Ruolo</Label>
          <p className="text-xs text-muted-foreground">
            Abilita per questo utente l&apos;accesso a gruppi di menu aggiuntivi (es. abilitare &quot;Studio Tecnico&quot; ad un Consulente HACCP).
            Lascia vuoto per usare solo i menu predefiniti del ruolo.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'studio', label: 'Studio Tecnico (ERP)' },
              { value: 'docenti', label: 'Formazione & Docenti' },
              { value: 'personnel', label: 'Personale & Documenti' },
              { value: 'haccp_group', label: 'HACCP Autocontrollo' },
              { value: 'documents', label: 'Documenti Sicurezza' },
              { value: 'admin', label: 'Amministrazione' },
            ].map((g) => (
              <label
                key={g.value}
                className="flex items-center gap-2 p-2 bg-white border border-violet-100 rounded-sm hover:border-violet-300 cursor-pointer"
              >
                <Checkbox
                  checked={(formData.extra_modules || []).includes(g.value)}
                  onCheckedChange={() => handleExtraModuleToggle(g.value)}
                  data-testid={`extra-module-${g.value}`}
                />
                <span className="text-sm text-violet-900">{g.label}</span>
              </label>
            ))}
          </div>
          {(formData.extra_modules || []).length > 0 && (
            <p className="text-xs text-violet-700">
              Moduli extra abilitati: <strong>{(formData.extra_modules || []).join(', ')}</strong>
            </p>
          )}
        </div>
      )}


      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" data-testid="user-submit-btn">
          {user ? 'Salva Modifiche' : 'Crea Utente'}
        </Button>
      </div>
    </form>
  );
};

const UserCard = ({ user, currentUserId, onEdit, onDelete, onResetPassword, onAssignPdv, onToggleActive, onManageKeys }) => {
  const roleInfo = ROLES.find(r => r.value === user.role)
    || { value: user.role, label: user.role === 'super_admin' ? 'Amministratore' : user.role, description: '', icon: Shield };
  const RoleIcon = roleInfo.icon;

  return (
    <Card className="hover-card" data-testid={`user-card-${user.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              user.role === 'super_admin' ? 'bg-primary text-primary-foreground' :
              user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
              'bg-secondary text-secondary-foreground'
            }`}>
              <RoleIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold">{user.full_name}</h3>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {user.email}
              </p>
            </div>
          </div>
          {user.id !== currentUserId && (
            <div className="flex gap-1">
              {['admin', 'collaborator', 'gerente', 'medico_competente', 'verificatore', 'consulente_haccp'].includes(user.role) && (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onAssignPdv(user)} title="Assegna Unità Locali">
                  <Store className="w-4 h-4" />
                </Button>
              )}
              {user.role === 'docente_online' && onManageKeys && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => onManageKeys(user)} title="Gestisci chiavi">
                  <Ticket className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(user)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onResetPassword(user)}>
                <Key className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${user.is_active ? 'text-amber-600' : 'text-green-600'}`}
                onClick={() => onToggleActive(user)}
                title={user.is_active ? 'Sospendi accesso (mancato pagamento)' : 'Riattiva accesso'}
                data-testid={`toggle-active-${user.id}`}
              >
                {user.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(user)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Badge variant={user.role === 'super_admin' ? 'default' : 'secondary'}>
            {roleInfo.label}
          </Badge>
          {['admin', 'collaborator', 'gerente', 'medico_competente', 'verificatore', 'consulente_haccp'].includes(user.role) && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Store className="w-3 h-3" />
              {user.allowed_pdv_ids?.length || 0} UL
            </Badge>
          )}
          {user.role === 'consulente_haccp' && (
            <Badge variant="outline" className="flex items-center gap-1 border-amber-400 text-amber-700">
              <Building2 className="w-3 h-3" />
              {user.allowed_azienda_ids?.length || 0} Aziende
            </Badge>
          )}
          {!user.is_active && (
            <Badge variant="destructive">Disattivo</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [createdUserCredentials, setCreatedUserCredentials] = useState(null);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);

  // === Docente Online: gestione chiavi ===
  const [keysDialogOpen, setKeysDialogOpen] = useState(false);
  const [keysUser, setKeysUser] = useState(null);
  const [keysData, setKeysData] = useState(null);
  const [grantQty, setGrantQty] = useState(1);

  const handleManageKeys = async (user) => {
    setKeysUser(user);
    setKeysDialogOpen(true);
    try {
      const res = await docenteKeysApi.getUserKeys(user.id);
      setKeysData(res.data);
    } catch (e) {
      toast.error('Errore caricamento chiavi');
    }
  };

  const handleGrantKeys = async () => {
    if (!keysUser || !grantQty) return;
    try {
      await docenteKeysApi.grantKeys(keysUser.id, { quantity: parseInt(grantQty, 10), reason: 'Assegnazione manuale' });
      toast.success(`${grantQty} chiavi assegnate`);
      const res = await docenteKeysApi.getUserKeys(keysUser.id);
      setKeysData(res.data);
      setGrantQty(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore assegnazione');
    }
  };
  const [assignPdvDialogOpen, setAssignPdvDialogOpen] = useState(false);
  const [userToAssignPdv, setUserToAssignPdv] = useState(null);
  const [selectedPdvIds, setSelectedPdvIds] = useState([]);

  const fetchData = async () => {
    try {
      const [usersRes, pdvsRes, aziendeRes] = await Promise.all([
        usersApi.getAll(),
        pdvApi.getAll(),
        api.get('/aziende').catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setPdvs(pdvsRes.data);
      setAziende(Array.isArray(aziendeRes.data) ? aziendeRes.data : []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per accedere a questa pagina');
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (data) => {
    try {
      if (editingUser) {
        const updatePayload = {
          full_name: data.full_name,
          phone: data.phone,
          role: data.role,
          allowed_pdv_ids: data.allowed_pdv_ids,
          allowed_azienda_ids: data.allowed_azienda_ids || [],
          allowed_modules: data.allowed_modules || [],
          extra_modules: data.extra_modules || [],
          visible_sections: data.visible_sections || [],
        };
        if (data.password && data.password.trim()) {
          updatePayload.password = data.password;
        }
        await usersApi.update(editingUser.id, updatePayload);
        toast.success('Utente aggiornato');
      } else {
        await usersApi.create(data);
        toast.success('Utente creato');
        // Show credentials dialog
        setCreatedUserCredentials({
          name: data.full_name,
          email: data.email,
          password: data.password,
          role: ROLES.find(r => r.value === data.role)?.label || data.role
        });
        setCredentialsDialogOpen(true);
      }
      setDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await usersApi.delete(userToDelete.id);
      toast.success('Utente eliminato');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleResetPasswordClick = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!userToResetPassword || !newPassword) return;
    try {
      await usersApi.resetPassword(userToResetPassword.id, newPassword);
      toast.success('Password aggiornata');
      setResetPasswordDialogOpen(false);
      setUserToResetPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Errore durante il reset della password');
    }
  };

  const handleAssignPdvClick = (user) => {
    setUserToAssignPdv(user);
    setSelectedPdvIds(user.allowed_pdv_ids || []);
    setAssignPdvDialogOpen(true);
  };

  const handleToggleActive = async (user) => {
    const willDeactivate = user.is_active !== false;
    const action = willDeactivate ? 'sospendere' : 'riattivare';
    const reason = willDeactivate
      ? 'L\'utente non potrà più accedere finché non lo riattivi.'
      : 'L\'utente potrà nuovamente accedere.';
    if (!window.confirm(`Vuoi ${action} l'accesso di "${user.full_name}"?\n\n${reason}`)) return;
    try {
      await usersApi.update(user.id, {
        full_name: user.full_name,
        role: user.role,
        is_active: !willDeactivate,
      });
      toast.success(willDeactivate ? `Accesso sospeso per ${user.full_name}` : `Accesso riattivato per ${user.full_name}`);
      fetchData();
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePdvToggleAssign = (pdvId) => {
    if (selectedPdvIds.includes(pdvId)) {
      setSelectedPdvIds(selectedPdvIds.filter(id => id !== pdvId));
    } else {
      setSelectedPdvIds([...selectedPdvIds, pdvId]);
    }
  };

  const handleSelectAllPdvsAssign = () => {
    if (selectedPdvIds.length === pdvs.length) {
      setSelectedPdvIds([]);
    } else {
      setSelectedPdvIds(pdvs.map(p => p.id));
    }
  };

  const handleAssignPdvConfirm = async () => {
    if (!userToAssignPdv) return;
    try {
      await usersApi.update(userToAssignPdv.id, {
        full_name: userToAssignPdv.full_name,
        role: userToAssignPdv.role,
        allowed_pdv_ids: selectedPdvIds
      });
      toast.success(`Assegnate ${selectedPdvIds.length} Unità Locali a ${userToAssignPdv.full_name}`);
      setAssignPdvDialogOpen(false);
      setUserToAssignPdv(null);
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'assegnazione');
    }
  };

  const filteredUsers = users.filter((user) => {
    const q = (search || '').toLowerCase();
    const name = (user.full_name || `${user.first_name || ''} ${user.last_name || ''}` || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const allowedRoles = ['super_admin', 'admin', 'amministratore', 'rspp'];
  if (!allowedRoles.includes(currentUser?.role)) {
    return (
      <div className="empty-state">
        <Shield className="empty-state-icon" />
        <p className="empty-state-title">Accesso negato</p>
        <p className="empty-state-description">Non hai i permessi per gestire gli utenti</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Gestione Utenti</h1>
          <p className="page-subtitle">{users.length} utenti registrati</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* v1.3.13 — Backfill utenti da UL pre-esistenti */}
          <Button
            variant="outline"
            onClick={async () => {
              if (!confirm('Per ogni Unità Locale (già esistente in Gestione Aziendale - UL) con una email valida ma SENZA un utente collegato in questa pagina, verrà creato automaticamente un account utente con:\n- Ruolo: Responsabile UL\n- Password iniziale: Password\n- Forzato cambio password al primo accesso\n\nGli utenti già esistenti NON vengono modificati né eliminati.\n\nProcedere?')) return;
              try {
                const r = await api.post('/admin/users/backfill-from-pdv');
                const s = r.data.summary || {};
                const parts = [];
                if (s.created > 0) parts.push(`✅ ${s.created} utenti creati`);
                if (s.linked_existing_user > 0) parts.push(`🔗 ${s.linked_existing_user} aggiornati (UL aggiunte ad utenti esistenti)`);
                if (s.skipped_no_email > 0) parts.push(`⚠️ ${s.skipped_no_email} UL saltate (senza email)`);
                if (s.already_complete > 0) parts.push(`ℹ️ ${s.already_complete} già a posto`);
                toast.success(parts.join(' · ') || 'Nessuna UL da sincronizzare', { duration: 12000 });
                if (s.skipped_no_email > 0) {
                  setTimeout(() => toast.warning(`Per le ${s.skipped_no_email} UL saltate aggiungi la email del referente da Gestione Aziendale → UL e ripeti la sincronizzazione.`, { duration: 8000 }), 1500);
                }
                if (Array.isArray(r.data.created) && r.data.created.length > 0) {
                  console.log('Utenti creati dal backfill:', r.data.created);
                }
                fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Backfill fallito');
              }
            }}
            data-testid="backfill-ul-users-btn"
            className="text-blue-700 border-blue-300 hover:bg-blue-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Sincronizza utenti da UL
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingUser(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
              </DialogTitle>
            </DialogHeader>
            <UserForm
              user={editingUser}
              pdvs={pdvs}
              aziende={aziende}
              isSuperAdmin={currentUser?.role === 'super_admin'}
              onSubmit={handleSubmit}
              onCancel={() => {
                setDialogOpen(false);
                setEditingUser(null);
              }}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="user-search-input"
        />
      </div>

      {/* Role Legend */}
      <div className="flex flex-wrap gap-4 mb-6 p-4 bg-secondary/30 rounded-sm">
        {ROLES.map((role) => (
          <div key={role.value} className="flex items-center gap-2 text-sm">
            <role.icon className="w-4 h-4" />
            <span className="font-medium">{role.label}:</span>
            <span className="text-muted-foreground">{role.description}</span>
          </div>
        ))}
      </div>

      {/* Users Grid */}
      {filteredUsers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <UserCard
                user={user}
                currentUserId={currentUser?.id}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onResetPassword={handleResetPasswordClick}
                onAssignPdv={handleAssignPdvClick}
                onToggleActive={handleToggleActive}
                onManageKeys={handleManageKeys}
              />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <p className="empty-state-title">Nessun utente trovato</p>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Utente</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l&apos;utente &quot;{userToDelete?.full_name}&quot;?
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Inserisci la nuova password per <strong>{userToResetPassword?.full_name}</strong>
            </p>
            <div className="space-y-2">
              <Label>Nuova Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleResetPasswordConfirm} disabled={!newPassword}>
                Aggiorna Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog - Show after creating a new user */}
      <Dialog open={credentialsDialogOpen} onOpenChange={setCredentialsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              Utente Creato con Successo
            </DialogTitle>
          </DialogHeader>
          {createdUserCredentials && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Condividi queste credenziali con il nuovo utente. <strong>La password non sarà più visibile.</strong>
              </p>
              
              <div className="bg-secondary p-4 rounded-sm space-y-3 font-mono text-sm">
                <div>
                  <span className="text-muted-foreground">Nome:</span>{' '}
                  <span className="font-semibold">{createdUserCredentials.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Ruolo:</span>{' '}
                  <span className="font-semibold">{createdUserCredentials.role}</span>
                </div>
                <div className="pt-2 border-t border-border">
                  <span className="text-muted-foreground">Email:</span>{' '}
                  <span className="font-semibold">{createdUserCredentials.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Password:</span>{' '}
                  <span className="font-semibold">{createdUserCredentials.password}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const text = `Credenziali SafeSuper RSPP\n\nNome: ${createdUserCredentials.name}\nRuolo: ${createdUserCredentials.role}\nEmail: ${createdUserCredentials.email}\nPassword: ${createdUserCredentials.password}\n\nURL: ${window.location.origin}`;
                    navigator.clipboard.writeText(text);
                    toast.success('Credenziali copiate negli appunti');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copia
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const printContent = `
                      <html>
                        <head><title>Credenziali Utente</title>
                        <style>body{font-family:Arial;padding:40px}h1{color:#1a365d}div{margin:10px 0;padding:10px;background:#f5f5f5}</style>
                        </head>
                        <body>
                          <h1>SafeSuper RSPP - Credenziali Accesso</h1>
                          <div><strong>Nome:</strong> ${createdUserCredentials.name}</div>
                          <div><strong>Ruolo:</strong> ${createdUserCredentials.role}</div>
                          <div><strong>Email:</strong> ${createdUserCredentials.email}</div>
                          <div><strong>Password:</strong> ${createdUserCredentials.password}</div>
                          <div><strong>URL:</strong> ${window.location.origin}</div>
                          <p style="margin-top:30px;color:#666">Si prega di cambiare la password al primo accesso.</p>
                        </body>
                      </html>
                    `;
                    const w = window.open('', '_blank');
                    w.document.write(printContent);
                    w.document.close();
                    w.print();
                  }}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Stampa
                </Button>
              </div>

              <Button className="w-full" onClick={() => setCredentialsDialogOpen(false)}>
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign PDV Dialog */}
      <Dialog open={assignPdvDialogOpen} onOpenChange={setAssignPdvDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assegna Unità Locali a {userToAssignPdv?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Selezionate: {selectedPdvIds.length} di {pdvs.length}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={handleSelectAllPdvsAssign}>
                {selectedPdvIds.length === pdvs.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </Button>
            </div>
            
            <div className="border rounded-md max-h-[400px] overflow-y-auto">
              {pdvs.map((pdv) => (
                <div
                  key={pdv.id}
                  className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                  onClick={() => handlePdvToggleAssign(pdv.id)}
                >
                  <Checkbox
                    checked={selectedPdvIds.includes(pdv.id)}
                    onCheckedChange={() => handlePdvToggleAssign(pdv.id)}
                  />
                  <Store className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{pdv.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{pdv.address}, {pdv.city}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setAssignPdvDialogOpen(false)}>
                Annulla
              </Button>
              <Button className="flex-1" onClick={handleAssignPdvConfirm}>
                <Check className="w-4 h-4 mr-2" />
                Salva ({selectedPdvIds.length} UL)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Docente Online: Gestione Chiavi */}
      <Dialog open={keysDialogOpen} onOpenChange={setKeysDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manage-keys-dialog">
          <DialogHeader>
            <DialogTitle>Gestione chiavi: {keysUser?.full_name}</DialogTitle>
          </DialogHeader>
          {keysData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded-md p-3 bg-emerald-50">
                  <div className="text-2xl font-bold text-emerald-700">{keysData.available}</div>
                  <div className="text-xs text-gray-600">Disponibili</div>
                </div>
                <div className="border rounded-md p-3 bg-gray-50">
                  <div className="text-2xl font-bold">{keysData.used}</div>
                  <div className="text-xs text-gray-600">Utilizzate</div>
                </div>
                <div className="border rounded-md p-3 bg-amber-50">
                  <div className="text-sm font-bold">
                    {keysData.subscription ? 'Abbonato' : 'No'}
                  </div>
                  <div className="text-xs text-gray-600">Abbonamento</div>
                </div>
              </div>
              <div className="border rounded-md p-3 bg-blue-50/50">
                <Label className="text-sm">Assegna chiavi manualmente</Label>
                <div className="flex gap-2 items-center mt-2">
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={grantQty}
                    onChange={(e) => setGrantQty(e.target.value)}
                    className="w-28"
                    data-testid="grant-qty-input"
                  />
                  <Button onClick={handleGrantKeys} data-testid="grant-keys-btn">
                    <Key className="w-4 h-4 mr-1" /> Assegna
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-sm">Storico chiavi</Label>
                <div className="border rounded-md mt-2 max-h-64 overflow-y-auto">
                  {keysData.keys?.length === 0 ? (
                    <p className="text-center py-6 text-gray-500 text-sm">Nessuna chiave</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Codice</th>
                          <th className="text-left p-2">Stato</th>
                          <th className="text-left p-2">Origine</th>
                          <th className="text-left p-2">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keysData.keys.map((k) => (
                          <tr key={k.id} className="border-t">
                            <td className="p-2 font-mono text-xs">{k.key_code}</td>
                            <td className="p-2">
                              <Badge className={k.status === 'unused' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200'}>
                                {k.status === 'unused' ? 'Libera' : 'Usata'}
                              </Badge>
                            </td>
                            <td className="p-2 text-xs">{k.source}</td>
                            <td className="p-2 text-xs">
                              {k.created_at ? new Date(k.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">Caricamento...</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
